import { useLayoutEffect, useRef, useState, type ComponentType } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import {
  Activity,
  Calendar,
  Dumbbell,
  Ellipsis,
  Flag,
  HeartPulse,
  ListChecks,
  RefreshCw,
  Settings,
  Sun,
  Trophy,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import { Logo } from '@/components/brand/Logo'
import { useAthletes, useMe } from '@/api/hooks'
import { Sheet } from '@/components/ui/Sheet'
import { ThemeToggle } from './ThemeToggle'
import { SyncStatusText } from './SyncStatusChip'

interface NavItem {
  key: string
  label: string
  icon: ComponentType<{ className?: string }>
  to: string
}

interface NavGroup {
  label: string
  items: NavItem[]
}

/**
 * The desktop rail (redesign, 2026-10-10; simplify pass, 2026-11-03): Train for the day-to-day and
 * the plan, Analyse for the trends, Settings below. Compare sits in the athlete switcher, Sync
 * behind the top bar's sync status and in Settings, Routes in Activities and the reviews in
 * Calendar.
 */
function buildNavGroups(slug: string | undefined): NavGroup[] {
  const base = `/a/${slug}`
  return [
    {
      label: 'Train',
      items: [
        { key: 'today', label: 'Today', icon: Sun, to: `${base}/today` },
        { key: 'plan', label: 'Plan', icon: Flag, to: `${base}/plan` },
        { key: 'calendar', label: 'Calendar', icon: Calendar, to: `${base}/calendar` },
        { key: 'activities', label: 'Activities', icon: ListChecks, to: `${base}/activities` },
      ],
    },
    {
      label: 'Analyse',
      items: [
        { key: 'fitness', label: 'Fitness', icon: Activity, to: `${base}/fitness` },
        { key: 'wellness', label: 'Wellness', icon: HeartPulse, to: `${base}/wellness` },
        { key: 'strength', label: 'Strength', icon: Dumbbell, to: `${base}/strength` },
        { key: 'records', label: 'Records', icon: Trophy, to: `${base}/records` },
      ],
    },
  ]
}

/**
 * Where a path belongs in the nav: route pages light up Activities (their tab), the month and
 * year reviews light up Calendar.
 */
function navPathOf(pathname: string): string {
  return pathname
    .replace(/^(\/a\/[^/]+)\/routes(\/|$)/, '$1/activities$2')
    .replace(/^(\/a\/[^/]+)\/review(\/|$)/, '$1/calendar$2')
}

/** The four items that fit in the mobile bottom tab bar; the rest live in the "More" sheet. */
function buildMobileTabs(slug: string | undefined): NavItem[] {
  const base = `/a/${slug}`
  return [
    { key: 'today', label: 'Today', icon: Sun, to: `${base}/today` },
    { key: 'plan', label: 'Plan', icon: Flag, to: `${base}/plan` },
    { key: 'calendar', label: 'Calendar', icon: Calendar, to: `${base}/calendar` },
    { key: 'fitness', label: 'Fitness', icon: Activity, to: `${base}/fitness` },
  ]
}

function buildMoreItems(slug: string | undefined, ownSlug: string | undefined): NavItem[] {
  const base = `/a/${slug}`
  return [
    { key: 'activities', label: 'Activities', icon: ListChecks, to: `${base}/activities` },
    { key: 'wellness', label: 'Wellness', icon: HeartPulse, to: `${base}/wellness` },
    { key: 'strength', label: 'Strength', icon: Dumbbell, to: `${base}/strength` },
    { key: 'records', label: 'Records', icon: Trophy, to: `${base}/records` },
    { key: 'settings', label: 'Settings', icon: Settings, to: `/a/${ownSlug}/settings` },
  ]
}

const linkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'group relative flex h-9 items-center gap-3 rounded-md px-3 text-body font-medium transition-colors duration-150',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
    isActive ? 'text-ink' : 'text-ink-2 hover:bg-surface-2/60 hover:text-ink',
  )

const iconClass = (active: boolean) =>
  cn(
    'size-4 shrink-0 transition-[color,transform] duration-200 ease-[var(--ease-spring)]',
    active ? 'text-accent' : 'group-hover:scale-110',
  )

const tabClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'group flex min-w-0 flex-1 flex-col items-center gap-1 px-0.5 pb-1.5 pt-2 text-micro font-medium transition-colors duration-150',
    'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent',
    isActive ? 'text-ink' : 'text-ink-2',
  )

/** The icon of a phone tab: an accent pill grows in behind it when the tab is active. */
function TabIcon({ icon: Icon, active }: { icon: NavItem['icon']; active: boolean }) {
  return (
    <span className="relative flex h-7 w-12 items-center justify-center">
      <span
        aria-hidden="true"
        className={cn(
          'absolute inset-0 rounded-full bg-[color-mix(in_oklab,var(--accent)_16%,transparent)] transition-[transform,opacity] duration-300 ease-[var(--ease-spring)]',
          active ? 'scale-100 opacity-100' : 'scale-50 opacity-0',
        )}
      />
      <Icon
        className={cn(
          'relative size-5 shrink-0 transition-[color,transform] duration-200 ease-[var(--ease-spring)] group-active:scale-90',
          active ? 'text-accent' : 'text-ink-2',
        )}
        aria-hidden="true"
      />
    </span>
  )
}

const moreTileClass = cn(
  'flex flex-col items-center justify-center gap-1.5 rounded-lg border border-line bg-surface px-2 py-3 text-small font-medium text-ink',
  'transition-[background-color,transform] duration-150 active:scale-[0.97] hover:bg-surface-2',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
)

/** The phone tab bar's sixth tab: a sheet with the pages that don't fit, appearance and sync. */
function MoreSheet({ items, active }: { items: NavItem[]; active: boolean }) {
  const [open, setOpen] = useState(false)
  const pathname = navPathOf(useLocation().pathname)

  return (
    <Sheet
      open={open}
      onOpenChange={setOpen}
      title="More"
      trigger={
        <button
          type="button"
          className={tabClass({ isActive: active || open })}
          aria-current={active ? 'page' : undefined}
        >
          <TabIcon icon={Ellipsis} active={active || open} />
          <span className="w-full truncate text-center">More</span>
        </button>
      }
    >
      <ul className="grid grid-cols-3 gap-2">
        {items.map((item) => {
          const current = pathname.startsWith(item.to)
          return (
            <li key={item.key}>
              <Link
                to={item.to}
                aria-current={current ? 'page' : undefined}
                className={cn(
                  moreTileClass,
                  current &&
                    'border-[color-mix(in_oklab,var(--accent)_45%,transparent)] bg-[color-mix(in_oklab,var(--accent)_10%,var(--surface))]',
                )}
                onClick={() => setOpen(false)}
              >
                <item.icon
                  className={cn('size-5 shrink-0', current ? 'text-accent' : 'text-ink-2')}
                  aria-hidden="true"
                />
                {item.label}
              </Link>
            </li>
          )
        })}
      </ul>
      <div className="mt-3 flex flex-col divide-y divide-line rounded-lg border border-line bg-surface">
        <div className="flex items-center justify-between gap-3 px-3 py-2.5">
          <span className="text-small font-medium text-ink">Appearance</span>
          <ThemeToggle />
        </div>
        <Link
          to="/sync"
          onClick={() => setOpen(false)}
          className="flex items-center justify-between gap-3 rounded-b-lg px-3 py-3 text-small font-medium text-ink hover:bg-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
        >
          <span className="inline-flex items-center gap-2.5">
            <RefreshCw className="size-4 text-ink-2" aria-hidden="true" />
            Sync
          </span>
          <SyncStatusText />
        </Link>
      </div>
    </Sheet>
  )
}

export interface NavRailProps {
  slug: string | undefined
}

/** Left rail on desktop (>=900px), bottom tab bar below it. Both render the same nav items. */
export function NavRail({ slug }: NavRailProps) {
  const { data: athletes } = useAthletes()
  const { data: me } = useMe()
  const effectiveSlug = slug ?? athletes?.[0]?.slug
  // Settings are the viewer's own, whichever athlete is on screen.
  const ownSlug = athletes?.find((a) => a.id === me?.athleteId)?.slug ?? effectiveSlug
  const groups = buildNavGroups(effectiveSlug)
  const items = groups.flatMap((g) => g.items)
  const mobileTabs = buildMobileTabs(effectiveSlug)
  const moreItems = buildMoreItems(effectiveSlug, ownSlug)
  const pathname = navPathOf(useLocation().pathname)
  const activeIndex = items.findIndex((item) => pathname.startsWith(item.to))
  // Measured rather than computed so the indicator lands on the row whatever the root font size.
  const listRef = useRef<HTMLUListElement>(null)
  const [indicatorTop, setIndicatorTop] = useState<number | null>(null)
  useLayoutEffect(() => {
    const row = listRef.current?.querySelectorAll<HTMLElement>('[data-nav-row]')[activeIndex]
    setIndicatorTop(row ? row.offsetTop : null)
  }, [activeIndex])
  // The first placement snaps; only moves between rows glide.
  const [glide, setGlide] = useState(false)
  useLayoutEffect(() => {
    if (indicatorTop == null || glide) return
    const id = requestAnimationFrame(() => setGlide(true))
    return () => cancelAnimationFrame(id)
  }, [indicatorTop, glide])

  return (
    <>
      <nav
        data-print-hide
        aria-label="Primary"
        className="hidden min-[900px]:sticky min-[900px]:top-0 min-[900px]:flex min-[900px]:h-dvh min-[900px]:w-[232px] min-[900px]:shrink-0 min-[900px]:flex-col min-[900px]:border-r min-[900px]:border-[var(--panel-border)] min-[900px]:bg-[color-mix(in_oklab,var(--surface)_55%,var(--bg))] min-[900px]:p-3"
      >
        <Link
          to={`/a/${effectiveSlug}/today`}
          aria-label="Trimetric, today"
          className="mb-3 flex items-center rounded-md px-3 py-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Logo size={28} animate className="text-lead" />
        </Link>
        <ul ref={listRef} className="relative flex flex-1 flex-col gap-0.5">
          {/* One shared highlight that glides to the active row instead of jumping. */}
          <li
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-x-0 top-0 h-9 rounded-md bg-[color-mix(in_oklab,var(--ink)_7%,transparent)]',
              glide && 'transition-[transform,opacity] duration-300 ease-[var(--ease-out)]',
            )}
            style={{
              transform: `translateY(${indicatorTop ?? 0}px)`,
              opacity: indicatorTop == null ? 0 : 1,
            }}
          >
            <span className="absolute inset-y-2 left-0 w-[3px] rounded-full bg-accent" />
          </li>
          {groups.map((group) => (
            <li key={group.label} className="flex flex-col gap-0.5 [&:not(:nth-child(2))]:mt-4">
              <span className="px-3 pb-1 text-caption font-medium text-ink-3">{group.label}</span>
              <ul className="flex flex-col gap-0.5">
                {group.items.map((item) => {
                  const active = items.indexOf(item) === activeIndex
                  return (
                    <li key={item.key} data-nav-row className="relative">
                      <NavLink
                        to={item.to}
                        className={(state) => linkClass({ isActive: state.isActive || active })}
                      >
                        <item.icon className={iconClass(active)} aria-hidden="true" />
                        {item.label}
                      </NavLink>
                    </li>
                  )
                })}
              </ul>
            </li>
          ))}
        </ul>
        <div className="my-2 border-t border-line" />
        <NavLink
          to={`/a/${ownSlug}/settings`}
          className={(state) => cn(linkClass(state), state.isActive && 'bg-surface-2')}
        >
          {({ isActive }) => (
            <>
              <Settings className={iconClass(isActive)} aria-hidden="true" />
              Settings
            </>
          )}
        </NavLink>
      </nav>

      <nav
        data-print-hide
        aria-label="Primary"
        className="fixed inset-x-0 bottom-0 z-40 flex border-t border-[var(--panel-border)] bg-[color-mix(in_oklab,var(--surface)_82%,transparent)] px-1 backdrop-blur-xl backdrop-saturate-150 min-[900px]:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {mobileTabs.map((item) => (
          <NavLink key={item.key} to={item.to} className={tabClass}>
            {({ isActive }) => (
              <>
                <TabIcon icon={item.icon} active={isActive} />
                <span className="w-full truncate text-center">{item.label}</span>
              </>
            )}
          </NavLink>
        ))}
        <MoreSheet
          items={moreItems}
          active={moreItems.some((item) => pathname.startsWith(item.to))}
        />
      </nav>
    </>
  )
}
